import { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  PlugZap,
  RefreshCw,
  Search,
  Smartphone,
  Unplug,
  XCircle,
} from "lucide-react";
import apiClient from "@/lib/api-client";
import { extractApiErrorMessage } from "@/lib/api-errors";
import { useGlobalToast } from "@/contexts/ToastContext";
import { useConfirm } from "@/contexts/ConfirmContext";

/**
 * Superadmin → IA → WhatsApp de siempre (7-oct-2026, petición de Luis: «la
 * gente no quiere cambiar de número»).
 *
 * Conecta a una sede el número de WhatsApp que el negocio YA usa, sin que deje
 * de funcionar en su app WhatsApp Business (la «coexistencia» de Meta). El
 * alta se hace acompañando al cliente: aquí se abre la ventana de Meta, quien
 * la abre elige la empresa donde queda el número y el dueño del teléfono
 * escanea un QR con su app.
 *
 * De momento es una pantalla de PRUEBA: enseña cada paso con lo que contesta
 * Meta, porque lo primero que hay que saber es qué deja hacer.
 */

// ── Lo mínimo de la librería de Meta que se usa aquí.
interface FacebookLoginResponse {
  authResponse?: { code?: string } | null;
  status?: string;
}
interface FacebookSdk {
  init(params: { appId: string; autoLogAppEvents: boolean; xfbml: boolean; version: string }): void;
  login(callback: (response: FacebookLoginResponse) => void, options: Record<string, unknown>): void;
}
declare global {
  interface Window {
    FB?: FacebookSdk;
    fbAsyncInit?: () => void;
  }
}

interface CoexistenceConfig {
  appId: string | null;
  configId: string | null;
  graphVersion: string;
}
interface LocationOption {
  id: string;
  name: string;
  organizationName: string;
  whatsappPhoneNumber: string | null;
  hasCoexistence: boolean;
}
interface Connection {
  id: string;
  locationId: string;
  wabaId: string;
  phoneNumberId: string;
  displayPhoneNumber: string;
  status: string;
  lastError: string | null;
  createdAt: string;
  location: { name: string; whatsappAiEnabled: boolean; organization: { name: string } };
}
interface ConnectStep {
  step: string;
  ok: boolean;
  detail: string;
}
/** Lo que dice la ventana de Meta mientras está abierta. */
interface SignupSession {
  event: string;
  wabaId: string | null;
  phoneNumberId: string | null;
}

/** Donde se recuerda el id de configuración entre visitas (no es un secreto). */
const CONFIG_ID_STORAGE_KEY = "rm.whatsappCoexistence.configId";
/** Lo que se espera a que la ventana diga qué cuenta es, tras devolver el código. */
const SESSION_WAIT_MS = 1500;

let sdkPromise: Promise<FacebookSdk> | null = null;

/** Carga la librería de Meta una sola vez. */
function loadFacebookSdk(appId: string, version: string): Promise<FacebookSdk> {
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise<FacebookSdk>((resolve, reject) => {
    window.fbAsyncInit = () => {
      if (!window.FB) {
        reject(new Error("La ventana de Meta no se ha cargado bien"));
        return;
      }
      window.FB.init({ appId, autoLogAppEvents: true, xfbml: false, version });
      resolve(window.FB);
    };
    const script = document.createElement("script");
    script.src = "https://connect.facebook.net/es_ES/sdk.js";
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";
    script.onerror = () => {
      sdkPromise = null;
      reject(new Error("No se pudo cargar la ventana de Meta (¿un bloqueador de anuncios?)"));
    };
    document.body.appendChild(script);
  });
  return sdkPromise;
}

function readStoredConfigId(): string {
  try {
    return window.localStorage.getItem(CONFIG_ID_STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
}

function storeConfigId(value: string): void {
  try {
    window.localStorage.setItem(CONFIG_ID_STORAGE_KEY, value);
  } catch {
    // Sin almacenamiento (ventana privada): habrá que volver a pegarlo.
  }
}

function hora(): string {
  return new Date().toLocaleTimeString("es-ES", { hour12: false });
}

export default function WhatsAppCoexistence() {
  const { showToast } = useGlobalToast();
  const { confirm } = useConfirm();

  const [config, setConfig] = useState<CoexistenceConfig | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [configId, setConfigId] = useState("");
  const [sdk, setSdk] = useState<FacebookSdk | null>(null);
  const [sdkError, setSdkError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [selected, setSelected] = useState<LocationOption | null>(null);

  const [connections, setConnections] = useState<Connection[] | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [steps, setSteps] = useState<ConnectStep[] | null>(null);
  const [events, setEvents] = useState<string[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);

  const sessionRef = useRef<SignupSession | null>(null);

  const log = useCallback((line: string) => {
    setEvents((previous) => [...previous, `${hora()}  ${line}`]);
  }, []);

  const loadConnections = useCallback(async () => {
    try {
      const res = await apiClient.get("/admin/whatsapp-coexistence/connections");
      setConnections(res.data?.data?.connections ?? []);
    } catch (err) {
      setConnections([]);
      showToast(extractApiErrorMessage(err, "No se pudieron cargar los números"), "error");
    }
  }, [showToast]);

  // Configuración, números ya conectados y la librería de Meta. La librería se
  // carga AL ENTRAR y no al pulsar el botón: la ventana de Meta tiene que
  // abrirse en el mismo clic o el navegador la bloquea.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await apiClient.get("/admin/whatsapp-coexistence/config");
        const data = res.data?.data as CoexistenceConfig;
        if (cancelled) return;
        setConfig(data);
        setConfigId(data.configId ?? readStoredConfigId());
        if (!data.appId) {
          setConfigError("El servidor no sabe cuál es la app de Meta");
          return;
        }
        try {
          const loaded = await loadFacebookSdk(data.appId, data.graphVersion);
          if (!cancelled) setSdk(loaded);
        } catch (err) {
          if (!cancelled) setSdkError(err instanceof Error ? err.message : "Error cargando Meta");
        }
      } catch (err) {
        if (!cancelled) setConfigError(extractApiErrorMessage(err, "No se pudo leer la configuración"));
      }
    })();
    void loadConnections();
    return () => {
      cancelled = true;
    };
  }, [loadConnections]);

  // Lo que va contando la ventana de Meta: al terminar dice qué cuenta y qué
  // número son; si se cierra o falla, en qué pantalla.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!event.origin.endsWith("facebook.com")) return;
      if (typeof event.data !== "string") return;
      try {
        const data = JSON.parse(event.data) as {
          type?: string;
          event?: string;
          data?: Record<string, unknown>;
        };
        if (data.type !== "WA_EMBEDDED_SIGNUP") return;
        const info = data.data ?? {};
        const text = (key: string) => (typeof info[key] === "string" ? (info[key] as string) : null);
        sessionRef.current = {
          event: data.event ?? "",
          wabaId: text("waba_id"),
          phoneNumberId: text("phone_number_id"),
        };
        log(`Meta: ${data.event ?? "(sin nombre)"} ${JSON.stringify(info)}`);
      } catch {
        // Otros mensajes de la ventana que no son para nosotros.
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [log]);

  // Buscador de sedes.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      void apiClient
        .get("/admin/whatsapp-coexistence/locations", { params: { search: search.trim() || undefined } })
        .then((res) => setLocations(res.data?.data?.locations ?? []))
        .catch(() => setLocations([]));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  const finishConnect = useCallback(
    async (code: string, locationId: string) => {
      // La ventana suele decir la cuenta ANTES de devolver el código; por si
      // llega justo después, se le da un momento. El código caduca en 30 s.
      if (!sessionRef.current?.wabaId) {
        await new Promise((resolve) => window.setTimeout(resolve, SESSION_WAIT_MS));
      }
      const session = sessionRef.current;
      try {
        const res = await apiClient.post("/admin/whatsapp-coexistence/connect", {
          locationId,
          code,
          wabaId: session?.wabaId ?? null,
          phoneNumberId: session?.phoneNumberId ?? null,
        });
        setSteps(res.data?.data?.steps ?? []);
        if (res.data?.success) {
          showToast("Número conectado. Carmen está apagada en él hasta que la enciendas.");
          setSelected(null);
        } else {
          showToast(res.data?.message ?? "No se pudo conectar el número", "error");
        }
      } catch (err) {
        showToast(extractApiErrorMessage(err, "No se pudo conectar el número"), "error");
      } finally {
        setConnecting(false);
        void loadConnections();
      }
    },
    [loadConnections, showToast],
  );

  const startConnect = () => {
    const id = configId.trim();
    if (!sdk || !selected || !id) return;
    storeConfigId(id);
    sessionRef.current = null;
    setSteps(null);
    setConnecting(true);
    const locationId = selected.id;
    log(`Abriendo la ventana de Meta para «${selected.name}»`);
    // La ventana de Meta no admite una función asíncrona aquí.
    sdk.login(
      (response) => {
        const code = response.authResponse?.code;
        if (!code) {
          log(`Meta no ha devuelto código (estado: ${response.status ?? "desconocido"})`);
          setConnecting(false);
          return;
        }
        log("Meta ha devuelto el código; terminando el alta");
        void finishConnect(code, locationId);
      },
      {
        config_id: id,
        response_type: "code",
        override_default_response_type: true,
        extras: {
          setup: {},
          // Sin esto la ventana ofrece dar de alta un número nuevo, no
          // conectar el que ya está en la app WhatsApp Business.
          featureType: "whatsapp_business_app_onboarding",
          sessionInfoVersion: "3",
        },
      },
    );
  };

  const toggleAi = async (connection: Connection) => {
    const enable = !connection.location.whatsappAiEnabled;
    if (enable) {
      const confirmed = await confirm({
        title: "Encender a Carmen en este número",
        message: `Carmen contestará a TODO el que escriba al ${connection.displayPhoneNumber}, como asistente de «${connection.location.name}». Solo se calla en las conversaciones en las que se conteste desde el móvil.`,
        confirmText: "Encender",
        cancelText: "Cancelar",
        danger: true,
      });
      if (!confirmed) return;
    }
    setBusyId(connection.id);
    try {
      const res = await apiClient.put(`/admin/whatsapp-coexistence/connections/${connection.id}/ai`, {
        enabled: enable,
      });
      showToast(res.data?.message ?? "Guardado");
      await loadConnections();
    } catch (err) {
      showToast(extractApiErrorMessage(err, "No se pudo guardar el cambio"), "error");
    } finally {
      setBusyId(null);
    }
  };

  const check = async (connection: Connection) => {
    setBusyId(connection.id);
    try {
      const res = await apiClient.post(`/admin/whatsapp-coexistence/connections/${connection.id}/check`);
      log(`Meta sobre ${connection.displayPhoneNumber}: ${res.data?.message ?? "sin respuesta"}`);
      showToast(res.data?.message ?? "Sin respuesta", res.data?.success ? "success" : "error");
    } catch (err) {
      showToast(extractApiErrorMessage(err, "No se pudo preguntar a Meta"), "error");
    } finally {
      setBusyId(null);
    }
  };

  const unlink = async (connection: Connection) => {
    const confirmed = await confirm({
      title: "Desenlazar este número",
      message: `«${connection.location.name}» se queda sin el ${connection.displayPhoneNumber} y Carmen deja de contestar en él. En el móvil hay que desconectarlo además desde WhatsApp Business: Ajustes → Cuenta → Plataforma para empresas.`,
      confirmText: "Desenlazar",
      cancelText: "Cancelar",
      danger: true,
    });
    if (!confirmed) return;
    setBusyId(connection.id);
    try {
      await apiClient.delete(`/admin/whatsapp-coexistence/connections/${connection.id}`);
      showToast("Número desenlazado");
      await loadConnections();
    } catch (err) {
      showToast(extractApiErrorMessage(err, "No se pudo desenlazar"), "error");
    } finally {
      setBusyId(null);
    }
  };

  const canConnect = Boolean(sdk && selected && configId.trim() && !connecting);

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-green-100 flex items-center justify-center">
          <Smartphone className="w-6 h-6 text-green-700" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">WhatsApp de siempre</h1>
          <p className="text-sm text-gray-500">
            Conecta el número que el negocio ya usa, sin que deje de funcionar en su móvil
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 flex gap-3">
        <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
        <div className="space-y-1">
          <p className="font-medium">Pantalla de prueba</p>
          <p>
            El número queda en la empresa de Meta que elijas en la ventana. Hace falta el móvil con
            WhatsApp Business de ese número para escanear el código QR. Al conectar, Carmen queda
            apagada: no contesta a nadie hasta que la enciendas abajo.
          </p>
        </div>
      </div>

      {/* Alta */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        <h2 className="font-semibold text-gray-900">Conectar un número</h2>

        {configError && <p className="text-sm text-red-600">{configError}</p>}
        {sdkError && <p className="text-sm text-red-600">{sdkError}</p>}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="coex-config">
            Id de la configuración de Meta
          </label>
          <input
            id="coex-config"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            value={configId}
            onChange={(e) => setConfigId(e.target.value.replace(/[^0-9]/g, ""))}
            placeholder="El número que da Meta al crear la configuración de inicio de sesión"
            inputMode="numeric"
            disabled={Boolean(config?.configId)}
          />
          <p className="text-xs text-gray-500 mt-1">
            App de Meta → Inicio de sesión con Facebook para empresas → Configuraciones.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="coex-search">
            Sede a la que se conecta
          </label>
          {selected ? (
            <div className="flex items-center justify-between gap-3 px-3 py-2 border border-primary-200 bg-primary-50 rounded-lg text-sm">
              <span className="min-w-0 truncate">
                <span className="font-medium text-gray-900">{selected.name}</span>
                <span className="text-gray-500"> · {selected.organizationName}</span>
              </span>
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="text-primary-700 font-medium hover:text-primary-800 flex-shrink-0"
              >
                Cambiar
              </button>
            </div>
          ) : (
            <>
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  id="coex-search"
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Busca por el nombre de la sede o del negocio"
                />
              </div>
              <ul className="mt-2 max-h-56 overflow-y-auto divide-y divide-gray-100 border border-gray-200 rounded-lg">
                {locations.length === 0 && (
                  <li className="px-3 py-2 text-sm text-gray-500">No hay sedes con ese nombre</li>
                )}
                {locations.map((location) => {
                  const blocked = Boolean(location.whatsappPhoneNumber) && !location.hasCoexistence;
                  return (
                    <li key={location.id}>
                      <button
                        type="button"
                        disabled={blocked}
                        onClick={() => setSelected(location)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50 disabled:hover:bg-white"
                      >
                        <span className="font-medium text-gray-900">{location.name}</span>
                        <span className="text-gray-500"> · {location.organizationName}</span>
                        {location.whatsappPhoneNumber && (
                          <span className="block text-xs text-gray-500">
                            {blocked
                              ? `Ya tiene un número nuestro (${location.whatsappPhoneNumber})`
                              : `Conectada con ${location.whatsappPhoneNumber}: se volvería a conectar`}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>

        <button
          type="button"
          onClick={startConnect}
          disabled={!canConnect}
          className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
        >
          {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlugZap className="w-4 h-4" />}
          {connecting ? "Esperando a Meta…" : "Abrir la ventana de Meta"}
        </button>
        {!sdk && !sdkError && !configError && (
          <p className="text-xs text-gray-500">Cargando la ventana de Meta…</p>
        )}

        {steps && (
          <ul className="space-y-1.5 pt-2 border-t border-gray-100">
            {steps.map((step, index) => (
              <li key={`${step.step}-${index}`} className="flex items-start gap-2 text-sm">
                {step.ok ? (
                  <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                )}
                <span className="min-w-0 break-words">
                  <span className="font-medium text-gray-900">{step.step}:</span>{" "}
                  <span className="text-gray-700">{step.detail}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Números conectados */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-gray-900">Números conectados</h2>
          <button
            type="button"
            onClick={() => void loadConnections()}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100"
            aria-label="Actualizar"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        {connections === null ? (
          <p className="text-sm text-gray-500">Cargando…</p>
        ) : connections.length === 0 ? (
          <p className="text-sm text-gray-500">Todavía no hay ninguno.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {connections.map((connection) => {
              const connected = connection.status === "connected";
              const aiOn = connection.location.whatsappAiEnabled;
              const busy = busyId === connection.id;
              return (
                <li key={connection.id} className="py-3 space-y-2">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="font-medium text-gray-900">{connection.displayPhoneNumber}</span>
                    <span className="text-sm text-gray-500">
                      {connection.location.name} · {connection.location.organization.name}
                    </span>
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        connected ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                      }`}
                    >
                      {connected ? "Conectado" : "Desconectado"}
                    </span>
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        aiOn ? "bg-violet-100 text-violet-800" : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {aiOn ? "Carmen contesta" : "Carmen apagada"}
                    </span>
                  </div>
                  {connection.lastError && (
                    <p className="text-xs text-red-600">{connection.lastError}</p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={busy || (!connected && !aiOn)}
                      onClick={() => void toggleAi(connection)}
                      className="px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {aiOn ? "Apagar a Carmen" : "Encender a Carmen"}
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void check(connection)}
                      className="px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Preguntar a Meta
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void unlink(connection)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50"
                    >
                      <Unplug className="w-4 h-4" />
                      Desenlazar
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Lo que ha ido pasando, tal cual */}
      {events.length > 0 && (
        <div className="bg-gray-900 rounded-xl p-4">
          <p className="text-xs font-medium text-gray-400 mb-2">Registro de esta sesión</p>
          <pre className="text-xs text-gray-100 whitespace-pre-wrap break-words font-mono">
            {events.join("\n")}
          </pre>
        </div>
      )}
    </div>
  );
}
